import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRightLeft, Check } from "lucide-react";
import {
  useEmployeeAbsences,
  useEmployeeEntitlements,
  useUpsertEntitlement,
} from "@/hooks/useEmployeeAbsences";
import { useEmploymentPeriods } from "@/hooks/useEmploymentPeriods";
import { usePublicHolidays } from "@/hooks/usePublicHolidays";
import { useOvertimeBalance, useSetCarryOver } from "@/hooks/useClockify";
import { countVacationDays, countBusinessDays } from "@/lib/date-utils";
import { computeYearlyEmploymentPct } from "./EntitlementCard";

interface YearEndBalanceCardProps {
  personId: string;
  year: number;
}

export function AbsenceCarryOverCard({
  personId,
  year,
}: YearEndBalanceCardProps) {
  const { data: absences = [] } = useEmployeeAbsences(personId, year);
  const { data: entitlements = [] } = useEmployeeEntitlements(personId);
  const { data: periods = [] } = useEmploymentPeriods(personId);
  const { data: overtime } = useOvertimeBalance(personId, year);
  const { data: nextYearOvertime } = useOvertimeBalance(personId, year + 1);
  const { data: publicHolidays = [] } = usePublicHolidays(year);
  const upsertEntitlement = useUpsertEntitlement();
  const setOvertimeCarryOver = useSetCarryOver();

  const publicHolidayDates = new Set(publicHolidays.map((h) => h.date));

  // --- Absence balance ---
  const entitlement = entitlements.find((e) => e.year === year);
  const baseVacationDays = entitlement?.vacation_days ?? 25;
  const absenceCarryIn = entitlement?.carry_over_days ?? 0;

  const employmentPct = computeYearlyEmploymentPct(periods, year);
  const vacationDays =
    Math.round(((baseVacationDays * employmentPct) / 100) * 10) / 10;

  const usedDays = absences
    .filter((h) => h.absence_type === "vacation")
    .reduce(
      (sum, h) =>
        sum +
        countVacationDays(
          h.start_date,
          h.end_date,
          publicHolidayDates,
          employmentPct,
        ),
      0,
    );

  const sickDays = absences
    .filter((h) => h.absence_type === "sick")
    .reduce((sum, h) => sum + countBusinessDays(h.start_date, h.end_date), 0);

  const absenceBalance =
    Math.round((vacationDays + absenceCarryIn - usedDays) * 10) / 10;

  // --- Carry-over into next year ---
  const nextYear = year + 1;
  const nextEntitlement = entitlements.find((e) => e.year === nextYear);
  const savedAbsenceCarryOver = nextEntitlement?.carry_over_days ?? 0;

  const [absenceEdit, setAbsenceEdit] = useState<string | null>(null);
  const [overtimeEdit, setOvertimeEdit] = useState<string | null>(null);

  const handleSaveAbsenceCarryOver = () => {
    const value = parseFloat(absenceEdit ?? "0");
    if (isNaN(value)) return;
    upsertEntitlement.mutate({
      person_id: personId,
      year: nextYear,
      vacation_days: nextEntitlement?.vacation_days ?? 25,
      carry_over_days: value,
    });
    setAbsenceEdit(null);
  };

  const handleApplyAbsenceBalance = () => {
    upsertEntitlement.mutate({
      person_id: personId,
      year: nextYear,
      vacation_days: nextEntitlement?.vacation_days ?? 25,
      carry_over_days: absenceBalance,
    });
  };

  // --- Overtime ---
  const overtimeBalance = overtime
    ? Math.round(overtime.balance * 100) / 100
    : null;
  const overtimeCarryIn = overtime?.carry_over ?? 0;
  const overtimeYearDelta = overtime?.year_delta ?? 0;
  const overtimePaidOut = overtime?.paid_out ?? 0;

  // Overtime carry-over into next year is stored as overtime_carry_over.year = year
  // (year Y stores the carry-over *into* Y+1)
  const handleSaveOvertimeCarryOver = () => {
    const value = parseFloat(overtimeEdit ?? "0");
    if (isNaN(value)) return;
    setOvertimeCarryOver.mutate(
      { personId, year, hours: value },
      { onSuccess: () => setOvertimeEdit(null) },
    );
  };

  const handleApplyOvertimeBalance = () => {
    if (overtimeBalance === null) return;
    setOvertimeCarryOver.mutate({ personId, year, hours: overtimeBalance });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4" />
          Year-End Balance {year}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Absence section */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Absences</h4>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Entitled (pro-rated)
              </span>
              <span>{vacationDays}</span>
            </div>
            {absenceCarryIn !== 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Carry-over into {year}
                </span>
                <span>
                  {absenceCarryIn > 0 ? "+" : ""}
                  {absenceCarryIn}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Vacation used</span>
              <span>-{usedDays}</span>
            </div>
            {sickDays > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sick days</span>
                <span>{sickDays}</span>
              </div>
            )}
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span>Vacation balance</span>
              <span>{absenceBalance}</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Carry-over into {nextYear}
            </span>
            {absenceEdit === null ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold">
                  {savedAbsenceCarryOver}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAbsenceEdit(String(savedAbsenceCarryOver))}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.5"
                  value={absenceEdit}
                  onChange={(e) => setAbsenceEdit(e.target.value)}
                  className="w-20 h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveAbsenceCarryOver();
                    if (e.key === "Escape") setAbsenceEdit(null);
                  }}
                />
                <Button
                  variant="default"
                  size="icon-sm"
                  onClick={handleSaveAbsenceCarryOver}
                  disabled={upsertEntitlement.isPending}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAbsenceEdit(null)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {savedAbsenceCarryOver !== absenceBalance && absenceEdit === null && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleApplyAbsenceBalance}
              disabled={upsertEntitlement.isPending}
            >
              Apply calculated balance ({absenceBalance}) as carry-over
            </Button>
          )}
        </div>

        {/* Overtime section */}
        {overtime && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Overtime</h4>
            <div className="text-sm space-y-1">
              {overtimeCarryIn !== 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Carry-over into {year}
                  </span>
                  <span>
                    {overtimeCarryIn >= 0 ? "+" : ""}
                    {overtimeCarryIn.toFixed(1)}h
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Year delta</span>
                <span>
                  {overtimeYearDelta >= 0 ? "+" : ""}
                  {overtimeYearDelta.toFixed(1)}h
                </span>
              </div>
              {overtimePaidOut > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Paid out</span>
                  <span>-{overtimePaidOut.toFixed(1)}h</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t pt-1 mt-1">
                <span>Overtime balance</span>
                <span>
                  {overtimeBalance !== null
                    ? `${overtimeBalance >= 0 ? "+" : ""}${overtimeBalance.toFixed(1)}h`
                    : "—"}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Carry-over into {nextYear}
              </span>
              {overtimeEdit === null ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">
                    {(nextYearOvertime?.carry_over ?? 0).toFixed(1)}h
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setOvertimeEdit(String(nextYearOvertime?.carry_over ?? 0))
                    }
                  >
                    Edit
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    value={overtimeEdit}
                    onChange={(e) => setOvertimeEdit(e.target.value)}
                    className="w-20 h-8 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveOvertimeCarryOver();
                      if (e.key === "Escape") setOvertimeEdit(null);
                    }}
                  />
                  <Button
                    variant="default"
                    size="icon-sm"
                    onClick={handleSaveOvertimeCarryOver}
                    disabled={setOvertimeCarryOver.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOvertimeEdit(null)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            {overtimeBalance !== null &&
              (nextYearOvertime?.carry_over ?? 0) !== overtimeBalance &&
              overtimeEdit === null && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleApplyOvertimeBalance}
                  disabled={setOvertimeCarryOver.isPending}
                >
                  Apply calculated balance ({overtimeBalance.toFixed(1)}h) as
                  carry-over
                </Button>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
